import { FC, useEffect, useRef, useState, type MouseEvent } from "react";
import { Button, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { AutoModel } from "../../components/common";
import { AlertMessage } from "../../components/common/AlertMessage";
// 预估积分先不展示
// import { CoinsEstimateIcon } from "../../components/common/CoinsEstimateIcon";
import { useAlert } from "../../hooks/useAlert";
import { triggerPointsRefresh } from "../../service/points";
import {
  DIGITAL_HUMAN_SCOPE,
  DIGITAL_HUMAN_TRAIN_STATUS,
  cloneDigitalHumanApi,
  deleteDigitalHumanApi,
  retryDigitalHumanApi,
  digitalHumanScopeLabel,
  digitalHumanTrainStatusText,
  digitalHumanUsable,
  estimateDigitalHumanPointsApi,
  resolveDigitalHumanCoverUrl,
  resolveDigitalHumanVideoUrl,
} from "../../service/digitalHuman";
import { type HumanFilter } from "./data";
import { useDigitalHumanPage } from "./useDigitalHumanPage";

const DigitalHumanCover: FC<{ src: string | null; videoSrc: string | null }> = ({
  src,
  videoSrc,
}) => {
  const [imgFailed, setImgFailed] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    setImgFailed(false);
    setReloadToken(0);
  }, [src]);

  useEffect(() => {
    setVideoFailed(false);
  }, [videoSrc]);

  // 图片偶发失败时先重试一次（换 query 绕过失败缓存），仍失败才回退视频首帧
  useEffect(() => {
    if (!imgFailed || reloadToken >= 1) return;
    const timer = window.setTimeout(() => {
      setReloadToken(1);
      setImgFailed(false);
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [imgFailed, reloadToken]);

  const finalImgFailed = imgFailed && reloadToken >= 1;
  const showImg = Boolean(src) && !finalImgFailed;
  const imgSrc = src
    ? reloadToken > 0
      ? `${src}${src.includes("?") ? "&" : "?"}_r=${reloadToken}`
      : src
    : "";
  const useVideo = !showImg;

  useEffect(() => {
    if (!useVideo || !videoSrc || videoFailed) return;
    const video = videoRef.current;
    if (!video) return;
    video.setAttribute("src", `${videoSrc}#t=0.1`);
    video.load();
  }, [useVideo, videoSrc, videoFailed]);

  return (
    <>
      <i className="adh-human-figure" />
      {showImg ? (
        <img
          className="adh-human-cover"
          src={imgSrc}
          alt=""
          onError={() => setImgFailed(true)}
        />
      ) : null}
      {useVideo && videoSrc && !videoFailed ? (
        <video
          ref={videoRef}
          className="adh-human-cover"
          muted
          playsInline
          preload="metadata"
          onLoadedMetadata={(event) => {
            try {
              event.currentTarget.currentTime = 0.12;
            } catch {
              /* ignore */
            }
          }}
          onError={() => setVideoFailed(true)}
        />
      ) : null}
    </>
  );
};

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const CloudUploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 18H6a4 4 0 0 1-1-8 7 7 0 0 1 13-3 5 5 0 0 1 1 10M12 21V10m-4 4 4-4 4 4" />
  </svg>
);

const TrashIcon = () => (
  <svg className="adh-ui-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M9 6V3h6v3M5 6l1 15h12l1-15M10 10v7m4-7v7" />
  </svg>
);

const RetryIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 7v5h-5M4 17v-5h5M6.1 6.1A8 8 0 0 1 20 12M4 12a8 8 0 0 0 13.9 5.9" />
  </svg>
);

// 重命名接口未接入
// const PencilIcon = () => (
//   <svg className="adh-ui-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
//     <path d="m15 4 5 5M4 20l5-1L21 7a2 2 0 0 0-5-5L4 14z" />
//   </svg>
// );

const MoreIcon = () => (
  <svg className="adh-ui-icon adh-more-horizontal-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="5" cy="12" r="1.6" />
    <circle cx="12" cy="12" r="1.6" />
    <circle cx="19" cy="12" r="1.6" />
  </svg>
);

const isPublicAvatar = (item: DigitalHuman.Item) =>
  item.isPublic === true || item.isPublic === 1 || item.isPublic === "1" || item.isPublic === "true";

const PHOTO_ACCEPT = "image/jpeg,image/png,.jpg,.jpeg,.png";
const VIDEO_ACCEPT = "video/mp4,video/quicktime,.mp4,.mov";
const CLONE_VIDEO_MAX_BYTES = 500 * 1024 * 1024;

const fileExt = (name: string) => {
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(idx + 1).toUpperCase() : "FILE";
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isAllowedCloneFile = (file: File, mode: "photo" | "video") => {
  const ext = fileExt(file.name).toLowerCase();
  if (mode === "photo") return ["jpg", "jpeg", "png"].includes(ext);
  return ["mp4", "mov"].includes(ext);
};

const readVideoDurationSeconds = (file: File): Promise<number> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      const duration = video.duration;
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(duration) && duration > 0 ? duration : 0);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(0);
    };
    video.src = url;
  });

const filterToScope = (filter: HumanFilter): DigitalHuman.Scope => {
  if (filter === "public") return DIGITAL_HUMAN_SCOPE.PUBLIC;
  if (filter === "mine") return DIGITAL_HUMAN_SCOPE.MINE;
  return DIGITAL_HUMAN_SCOPE.ALL;
};

export const AiDigitalHumanAvatars: FC = () => {
  const navigate = useNavigate();
  const { alertConfig, hideAlert, showWarning, showSuccess } = useAlert();
  const [filter, setFilter] = useState<HumanFilter>("all");
  const [search, setSearch] = useState("");
  const [debouncedName, setDebouncedName] = useState("");
  const [modal, setModal] = useState(false);
  const [cloneMode, setCloneMode] = useState<"photo" | "video">("video");
  const [cloneName, setCloneName] = useState("");
  const [cloneFile, setCloneFile] = useState<File | null>(null);
  const [cloneConsent, setCloneConsent] = useState(false);
  const [cloneSubmitting, setCloneSubmitting] = useState(false);
  const [estimatedPoints, setEstimatedPoints] = useState<number | null>(null);
  const [estimatingPoints, setEstimatingPoints] = useState(false);
  const [deleting, setDeleting] = useState<DigitalHuman.Item | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [retryingId, setRetryingId] = useState<number | null>(null);
  const cloneFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedName(search.trim()), 400);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const closeIfOutside = (event: Event) => {
      const target = event.target as Node | null;
      document.querySelectorAll<HTMLDetailsElement>(".adh-human-actions[open]").forEach((menu) => {
        if (!target || !menu.contains(target)) menu.open = false;
      });
    };
    const closeOnEsc = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      document.querySelectorAll<HTMLDetailsElement>(".adh-human-actions[open]").forEach((menu) => {
        menu.open = false;
      });
    };
    document.addEventListener("mousedown", closeIfOutside);
    document.addEventListener("keydown", closeOnEsc);
    return () => {
      document.removeEventListener("mousedown", closeIfOutside);
      document.removeEventListener("keydown", closeOnEsc);
    };
  }, []);

  const { list, loading, loadingMore, hasMore, hasPending, sentinelRef, reload, refresh } =
    useDigitalHumanPage({
      scope: filterToScope(filter),
      name: debouncedName,
      pageSize: 20,
    });

  // 克隆任务是完成后才扣积分，轮询到没有进行中的任务时再刷新一次
  const prevPendingRef = useRef(false);
  useEffect(() => {
    if (prevPendingRef.current && !hasPending) triggerPointsRefresh();
    prevPendingRef.current = hasPending;
  }, [hasPending]);

  const closeCloneModal = () => {
    if (cloneSubmitting) return;
    setModal(false);
    setCloneMode("video");
    setCloneName("");
    setCloneFile(null);
    setCloneConsent(false);
    setEstimatedPoints(null);
    setEstimatingPoints(false);
    if (cloneFileInputRef.current) cloneFileInputRef.current.value = "";
  };

  useEffect(() => {
    if (!modal || cloneMode !== "video" || !cloneFile) {
      setEstimatedPoints(null);
      setEstimatingPoints(false);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setEstimatingPoints(true);
      const durationSeconds = await readVideoDurationSeconds(cloneFile);
      if (cancelled) return;
      try {
        const res = await estimateDigitalHumanPointsApi(
          durationSeconds > 0 ? Math.round(durationSeconds) : 0
        );
        if (cancelled) return;
        const points = Number(res.data);
        setEstimatedPoints(Number.isFinite(points) ? points : 0);
      } catch {
        if (!cancelled) setEstimatedPoints(null);
      } finally {
        if (!cancelled) setEstimatingPoints(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [modal, cloneMode, cloneFile]);

  const pickCloneFile = (file: File | undefined) => {
    if (!file) return;
    if (!isAllowedCloneFile(file, cloneMode)) {
      showWarning(cloneMode === "photo" ? "请上传 JPG 或 PNG 图片" : "请上传 MP4 或 MOV 视频");
      return;
    }
    if (cloneMode === "video" && file.size > CLONE_VIDEO_MAX_BYTES) {
      showWarning(`视频大小需在 500MB 以内（包括 500MB），当前：${formatFileSize(file.size)}`);
      return;
    }
    setCloneFile(file);
  };

  const submitClone = async () => {
    const name = cloneName.trim();
    if (!name || !cloneFile || !cloneConsent || cloneSubmitting) return;
    setCloneSubmitting(true);
    try {
      await cloneDigitalHumanApi(
        { name, cloneType: "v2" },
        cloneFile
      );
      setModal(false);
      setCloneMode("video");
      setCloneName("");
      setCloneFile(null);
      setCloneConsent(false);
      if (cloneFileInputRef.current) cloneFileInputRef.current.value = "";
      setFilter("mine");
      reload();
      triggerPointsRefresh();
      showSuccess("克隆任务已提交，完成后可使用");
    } catch {
      /* request 已弹出错误 */
    } finally {
      setCloneSubmitting(false);
    }
  };

  const openDeleteAvatar = (item: DigitalHuman.Item, event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const menu = event.currentTarget.closest("details");
    if (menu) menu.open = false;
    setDeleting(item);
  };

  const retryClone = async (item: DigitalHuman.Item) => {
    if (retryingId != null) return;
    setRetryingId(item.id);
    try {
      await retryDigitalHumanApi(item.id);
      refresh();
      triggerPointsRefresh();
      showSuccess("已提交重试，完成后才可使用");
    } catch {
      /* request 已弹出错误 */
    } finally {
      setRetryingId(null);
    }
  };

  const closeDeleteModal = () => {
    if (deleteSubmitting) return;
    setDeleting(null);
  };

  const confirmDeleteAvatar = async () => {
    if (!deleting || deleteSubmitting) return;
    setDeleteSubmitting(true);
    try {
      await deleteDigitalHumanApi(deleting.id);
      setDeleting(null);
      reload();
      showSuccess("形象已删除");
    } catch {
      /* request 已弹出错误 */
    } finally {
      setDeleteSubmitting(false);
    }
  };

  return (
    <div className="adh-page">
      <AlertMessage config={alertConfig} onClose={hideAlert} />
      <header className="adh-page-header">
        <div>
          <span className="adh-eyebrow">AVATARS</span>
          <h1>我的数字人</h1>
          <p>查看公共形象，管理我的形象。</p>
        </div>
        <div className="adh-page-actions">
          <button className="adh-secondary-btn" type="button" onClick={() => navigate("/hub/ai-digital-human")}>返回创作</button>
          <button className="adh-primary-btn" type="button" onClick={() => setModal(true)}>
            <PlusIcon /> 克隆新形象
          </button>
        </div>
      </header>

      <div className="adh-toolbar">
        <div className="adh-filter-tabs">
          <button className={filter === "all" ? "active" : ""} type="button" onClick={() => setFilter("all")}>全部</button>
          <button className={filter === "public" ? "active" : ""} type="button" onClick={() => setFilter("public")}>公共形象</button>
          <button className={filter === "mine" ? "active" : ""} type="button" onClick={() => setFilter("mine")}>我的形象</button>
        </div>
        <label className="adh-search">
          <SearchIcon />
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="搜索数字人名称" />
        </label>
      </div>

      <Spin spinning={loading && list.length === 0}>
        {list.length === 0 && !loading ? (
          <p className="adh-picker-empty">没有符合条件的数字人</p>
        ) : (
          <div className="adh-human-grid">
            {list.map((a) => {
              const cover = resolveDigitalHumanCoverUrl(a);
              const coverVideo = resolveDigitalHumanVideoUrl(a);
              const status = digitalHumanTrainStatusText(a.trainStatus);
              const mine = !isPublicAvatar(a);
              const failed = String(a.trainStatus) === DIGITAL_HUMAN_TRAIN_STATUS.FAILED;
              const done = String(a.trainStatus) === DIGITAL_HUMAN_TRAIN_STATUS.DONE;
              const canDelete = mine && (done || failed);
              return (
                <article
                  key={a.id}
                  className="adh-human-card"
                  data-avatar-scope={mine ? "mine" : "public"}
                >
                  <div className="adh-human-visual">
                    <DigitalHumanCover src={cover} videoSrc={coverVideo} />
                    <span className="adh-avatar-status" role="status">{status}</span>
                    {filter === "all" ? (
                      <span className="adh-scope-tag">{digitalHumanScopeLabel(a)}</span>
                    ) : null}
                    {failed ? (
                      <button
                        type="button"
                        className="adh-human-retry"
                        aria-label={`重试${a.name}`}
                        title="重试"
                        disabled={retryingId === a.id}
                        onClick={() => void retryClone(a)}
                      >
                        <RetryIcon />
                      </button>
                    ) : null}
                  </div>
                  <div className="adh-human-info">
                    <span>
                      <span className="adh-human-title-row">
                        <strong>{a.name}</strong>
                        {/* 重命名接口未接入
                        {mine ? (
                          <button
                            className="adh-human-rename"
                            type="button"
                            aria-label={`重命名${a.name}`}
                            title="重命名"
                          >
                            <PencilIcon />
                          </button>
                        ) : null}
                        */}
                      </span>
                      <small>{digitalHumanUsable(a) ? "可用于口播视频与视频课" : "完成后才可使用"}</small>
                    </span>
                    {canDelete ? (
                      <details className="adh-human-actions" name="adh-avatar-actions">
                        <summary aria-label={`${a.name}的操作`} title="更多操作">
                          <MoreIcon />
                        </summary>
                        <div className="adh-human-action-list">
                          <button
                            className="adh-delete-avatar-button"
                            type="button"
                            onClick={(event) => openDeleteAvatar(a, event)}
                          >
                            <TrashIcon />
                            删除
                          </button>
                        </div>
                      </details>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </Spin>
      {list.length > 0 ? (
        <div className="adh-infinite-status" ref={sentinelRef} aria-hidden={!loadingMore && hasMore}>
          {loadingMore ? "加载中…" : hasMore ? "下滑加载更多" : "没有更多了"}
        </div>
      ) : null}

      <AutoModel
        open={modal}
        onClose={closeCloneModal}
        title={
          <div className="adh-writing-title">
            <span className="adh-eyebrow">AVATAR CLONE</span>
            <span>克隆新形象</span>
            <p>请确保已获得出镜人的明确授权。</p>
          </div>
        }
        size="md"
        contentMaxWidth={620}
        contentClassName="adh-clone-shell"
        bodyClassName="adh-clone-body"
        footerLeft={
          /* 预估积分先不展示
          cloneMode === "video" && cloneFile && estimatingPoints ? (
            <span className="adh-cost-estimate">积分预估中…</span>
          ) : cloneMode === "video" && cloneFile && estimatedPoints != null ? (
            <span className="adh-cost-estimate" title="以实际扣减为准">
              <CoinsEstimateIcon />
              预计扣除 {estimatedPoints} 积分
            </span>
          ) : (
            <span className="adh-clone-hint">提交后开始克隆，完成后才可使用</span>
          )
          */
          <span className="adh-clone-hint">提交后开始克隆，完成后才可使用</span>
        }
        footerRight={
          <Button
            type="primary"
            loading={cloneSubmitting}
            disabled={!cloneName.trim() || !cloneFile || !cloneConsent || cloneSubmitting}
            onClick={() => void submitClone()}
          >
            提交克隆任务
          </Button>
        }
      >
        {/* 克隆方式只剩视频，暂不展示切换 Tab；照片创建待图片上传接口接入后再开放
        <div className="adh-clone-tabs">
          <button
            className={cloneMode === "photo" ? "active" : ""}
            type="button"
            disabled={cloneSubmitting}
            onClick={() => {
              setCloneMode("photo");
              setCloneFile(null);
              if (cloneFileInputRef.current) cloneFileInputRef.current.value = "";
            }}
          >
            照片创建
          </button>
          <button
            className={cloneMode === "video" ? "active" : ""}
            type="button"
            disabled={cloneSubmitting}
            onClick={() => {
              setCloneMode("video");
              setCloneFile(null);
              if (cloneFileInputRef.current) cloneFileInputRef.current.value = "";
            }}
          >
            视频克隆
          </button>
        </div>
        */}
        <label className="adh-form-field">
          <span>数字人名称 <b>*</b></span>
          <input
            maxLength={100}
            value={cloneName}
            disabled={cloneSubmitting}
            onChange={(e) => setCloneName(e.target.value)}
            placeholder="例如：企业讲师小林"
          />
        </label>
        <input
          ref={cloneFileInputRef}
          type="file"
          accept={cloneMode === "photo" ? PHOTO_ACCEPT : VIDEO_ACCEPT}
          hidden
          onChange={(e) => {
            pickCloneFile(e.target.files?.[0]);
            e.currentTarget.value = "";
          }}
        />
        {cloneFile ? (
          <div className="adh-file-card">
            <span className="adh-file-type">{fileExt(cloneFile.name)}</span>
            <span>
              <strong>{cloneFile.name}</strong>
              <small>{formatFileSize(cloneFile.size)}</small>
            </span>
            <button
              type="button"
              aria-label="删除上传素材"
              disabled={cloneSubmitting}
              onClick={() => {
                setCloneFile(null);
                if (cloneFileInputRef.current) cloneFileInputRef.current.value = "";
              }}
            >
              <TrashIcon />
            </button>
          </div>
        ) : (
          <button
            className="adh-upload-zone"
            type="button"
            disabled={cloneSubmitting}
            onClick={() => cloneFileInputRef.current?.click()}
          >
            <span className="adh-upload-icon">
              <CloudUploadIcon />
            </span>
            <strong>{cloneMode === "photo" ? "上传正面清晰的半身照片" : "上传已授权的人物视频"}</strong>
            <small>{cloneMode === "photo" ? "JPG、PNG · 仅一人出镜 · 面部无遮挡" : "MP4、MOV · 支持(包括) 500MB · 正面半身出镜"}</small>
          </button>
        )}
        <div className="adh-consent-box">
          <label>
            <input
              type="checkbox"
              checked={cloneConsent}
              disabled={cloneSubmitting}
              onChange={(e) => setCloneConsent(e.target.checked)}
            />
            <span>我确认已获得出镜人对数字人形象制作和使用的明确授权。</span>
          </label>
        </div>
      </AutoModel>

      <AutoModel
        open={Boolean(deleting)}
        onClose={closeDeleteModal}
        title="删除形象"
        size="sm"
        footerRight={
          <>
            <Button disabled={deleteSubmitting} onClick={closeDeleteModal}>
              取消
            </Button>
            <Button
              type="primary"
              danger
              loading={deleteSubmitting}
              onClick={() => void confirmDeleteAvatar()}
            >
              确认删除
            </Button>
          </>
        }
      >
        <p className="adh-delete-avatar-copy">
          确定删除“{deleting?.name}”？删除后将从我的形象中移除，已有作品会保留。
        </p>
      </AutoModel>
    </div>
  );
};
